import { Header } from '@latitude/header';
import { IconButton } from '@latitude/icon-button';
import { Icon } from '@latitude/icons';
import { cn } from '@latitude/token-utils';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import type { DrawerHeaderProps } from 'types';

/**
 * Header of the side panel. A thin adapter over the shared `@latitude/header`,
 * mapping the drawer's props onto it and supplying the Radix `Close` wiring
 * that package deliberately does not know about.
 */
export const SheetHeader: FC<PropsWithChildren<DrawerHeaderProps>> = ({
  className,
  backButton = false,
  leadingElement = undefined,
  extraHeaderContent,
  title,
  description,
  closeOnX,
  truncateTitle = false,
}) => {
  // extraHeaderContent has no Figma counterpart, so it stacks above the title
  // through the wrapper slot instead of adding a slot to the shared header.
  // No memo needed: titleWrapper is a render prop, so a fresh function each
  // render costs nothing and cannot remount the title.
  const titleWrapper = extraHeaderContent
    ? (titleNode: ReactNode) => (
        <div className="flex min-w-0 flex-col">
          {extraHeaderContent}
          {titleNode}
        </div>
      )
    : undefined;

  const leading = backButton ? (
    <SheetPrimitive.Close asChild>
      <IconButton
        data-testid="back-close-button"
        aria-label="Back"
        icon={<Icon name="arrow-left" />}
        variant="strong"
        type="ghost"
        size="small"
      />
    </SheetPrimitive.Close>
  ) : (
    leadingElement && (
      <span data-testid="header-leading-element">{leadingElement}</span>
    )
  );

  return (
    <Header
      data-testid="drawer-header"
      className={cn('flex-none', className)}
      title={title}
      titleWrapper={titleWrapper}
      truncateTitle={truncateTitle}
      subtext={description}
      leadingElement={leading}
      actions={
        <SheetPrimitive.Close asChild>
          <IconButton
            data-testid="close-on-x-button"
            aria-label="Close"
            icon={<Icon name="x-close" />}
            variant="strong"
            type="ghost"
            size="small"
            onClick={() => {
              closeOnX?.();
            }}
          />
        </SheetPrimitive.Close>
      }
    />
  );
};

SheetHeader.displayName = 'SheetHeader';
